import React, {useState, useEffect, useRef} from 'react'
import styled, {css} from 'styled-components'

import {getCookie} from '@miro-site/core'
import {useSplitIo} from '@miro-site/splitio'
import {Box, Close, Button, useWindowSize, Text} from '@miro-site/core'
import {theme} from '@miro-site/design-tokens'
import {Content} from '@miro-site/components_utils'
import {useServicesContext} from '@miro-site/services'

const FloatingContainer = styled(Box)<{$isVisible?: boolean}>`
  position: fixed;
  bottom: 16px;
  right: -360px;
  width: 360px;
  z-index: -10;
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.5s, right 0.5s;
  ${({$isVisible}) => {
    return (
      $isVisible &&
      css`
        right: 16px;
        visibility: visible;
        opacity: 1;
        z-index: 99;
      `
    )
  }}
`

const RoundedBox = styled(Box).attrs({
  $styles: {
    borderRadius: theme.spaces.sm,
  },
})`
  padding: 28px 24px;
  box-shadow: 0px 4px 16px rgba(5, 0, 56, 0.12);
  background: white;
  cursor: pointer;
`

const CloseIconContainer = styled(Box)`
  border-radius: 50%;
  width: 24px;
  height: 24px;
  padding: 4px;
  background: #b4b3c3;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  right: 8px;
  top: 8px;
  cursor: pointer;
  transition: background 0.3s ease;
  svg {
    width: 24px;
    height: 24px;
  }
  &:hover {
    background: #2d50fa;
  }
`
const TitleStyles = styled(Box)`
  p {
    font-weight: 500;
    font-size: 20px;
    line-height: 28px;
  }
`

const TitleContainer = styled(TitleStyles)`
  p {
    color: ${theme.colors.primaryBlue};
  }
`
const SubTitleContainer = styled(TitleStyles)`
  p {
    margin-bottom: 8px;
  }
`
const DataContainer = styled(Box)`
  p {
    font-size: 16px;
    line-height: 24px;
  }
`
const CtaContainer = styled(Box)`
  margin-top: 24px;
  margin-bottom: 8px;
`

interface ContentProps {
  title: string
  subTitle: string
  items: string[]
  cta?: string
  helpText?: string
}

const PopupBanner: React.FC = () => {
  const [content, setContent] = useState<ContentProps>({title: '', subTitle: '', items: []})
  const {analytics} = useServicesContext()
  const containerRef = useRef<HTMLDivElement>(null)
  const [isBannerVisible, setBannerVisibility] = useState(false)
  const isSplitAlreadyCalled = useRef<boolean>()
  const {width} = useWindowSize()
  const isDeskTopSize = width > theme.breakpoints.v2Tablet
  const [splitStatus, splitIoClient] = useSplitIo()
  const positionToShowBanner = 500
  const experimentName = 'EXP-LPX-HOME-013'
  const bannerDismissedSessionKey = 'SLIDE_IN_BANNER_DISMISSED'

  useEffect(() => {
    const alreadyBannerDismissed = parseInt(
      sessionStorage.getItem(bannerDismissedSessionKey) || '0',
    )
    if (
      alreadyBannerDismissed ||
      !isDeskTopSize ||
      isSplitAlreadyCalled.current ||
      splitStatus.status !== 'READY' ||
      !splitIoClient
    ) {
      return
    }

    const experimentAttributes = JSON.parse(getCookie('experiment_attributes') || '{}')
    const experiment = splitIoClient.getTreatmentWithConfig(experimentName, experimentAttributes)
    const config = experiment.config ? JSON.parse(experiment.config) : {}
    isSplitAlreadyCalled.current = true
    if (experiment.treatment !== 'off' && experiment.treatment !== 'control') {
      window.addEventListener('oneTrustLoaded', () => {
        window.addEventListener('scroll', onWindowScroll)
        /**
         * checking on load is already page scrolled,
         * Adding a delay since most of the times if user just scrolls without moving and then,
         * moves the cursor both cookie banner and this slide in banner are coming at a time,
         * so adding some delay
         */
        setTimeout(() => {
          onWindowScroll()
        }, 500)
      })
      setContent(config)
    }
  }, [splitStatus])

  const closeTheBanner = e => {
    analytics.sendCustomEvent('ELH014-CLICK-CLOSE')
    setBannerVisibility(false)
    sessionStorage.setItem(bannerDismissedSessionKey, '1')
    //ParentContainer have a click event which redirects to signup, stoping that propagation
    e.stopPropagation()
  }

  const onButtonClick = (
    e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault()
    analytics.sendCustomEvent('ELH014-CLICK-SIGNUP')
    window.location.href = '/signup'
  }

  const onWindowScroll = () => {
    if (window.scrollY >= positionToShowBanner && !isBannerVisible) {
      const banner = document.getElementById('onetrust-banner-sdk')
      const oneTrustBannerIsVisible = banner?.offsetWidth !== 0 || banner?.offsetHeight !== 0
      let popupBannerHeightFix = '16px'
      if (oneTrustBannerIsVisible) {
        popupBannerHeightFix = `${banner.offsetHeight + 32}px`
      }
      containerRef.current.style.bottom = popupBannerHeightFix
      analytics.sendCustomEvent('ELH014-SCROLL-VISIBLE')
      setBannerVisibility(true)
      window.removeEventListener('scroll', onWindowScroll)
    }
  }

  return (
    <FloatingContainer ref={containerRef} $isVisible={isBannerVisible}>
      <RoundedBox onClick={onButtonClick}>
        <CloseIconContainer onClick={closeTheBanner} title="close">
          <Close bold color="primaryWhite" />
        </CloseIconContainer>
        <TitleContainer>
          <Content size="small" html={content.title} />
        </TitleContainer>
        <SubTitleContainer>
          <Content size="small" color="dark" html={content.subTitle} />
        </SubTitleContainer>
        {content.items && content.items.length && (
          <DataContainer>
            {content.items.map((item, i) => (
              <Content key={i} size="small" color="grey" html={'• ' + item} />
            ))}
          </DataContainer>
        )}

        <CtaContainer>
          <Button
            $withArrow
            $buttonType="primary"
            $size="small"
            href="/signup"
            onClick={onButtonClick}
          >
            {content.cta || 'Sign up for free'}
          </Button>
        </CtaContainer>
        <Text $color="greyish" $variant="p2">
          {content.helpText || 'Free forever — no credit card required'}
        </Text>
      </RoundedBox>
    </FloatingContainer>
  )
}

export default PopupBanner
